import { useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import {
  CalendarPlus,
  Check,
  ChevronsUpDown,
  Mail,
  Paperclip,
  Users,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DatePicker } from '@/components/ui/date-picker'
import { cn } from '@/lib/utils'
import {
  LEAVE_TYPES,
  HALF_DAY_OPTIONS,
  UNPAID_LEAVE_TYPE,
  computeLeaveDaysPreview,
  type HalfDayOption,
} from '@/constant/leave'
import {
  useGetPublicEmployeesQuery,
  useSendPublicLeaveOtpMutation,
  useConfirmPublicLeaveOtpMutation,
  useGetPublicLeaveBalancesQuery,
  useCreatePublicLeaveRequestMutation,
  useUploadPublicLeaveAttachmentMutation,
  useEmailPublicLeaveReportMutation,
  type PublicEmployee,
} from '@/redux/service/publicLeave'

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024
// send-otp is rate-limited to 3 requests per 5 minutes
// (routes/publicLeave.routes.js) - this cooldown keeps rapid re-clicks from
// hitting that limit and surfacing a raw "Too many requests" error.
const RESEND_COOLDOWN_SECONDS = 30

interface PublicLeaveFormValues {
  leave_type: string
  from_date: string
  from_half: HalfDayOption
  to_date: string
  to_half: HalfDayOption
  reason: string
  notify: number[]
  attachment: File | null
}

// Standalone, unauthenticated page — no PrivatePage/PublicPage wrapper, no
// DashboardLayout. Meant to be shared as a plain link (e.g. in a ClickUp DM
// or chat channel) so someone can file a leave request without signing into
// the dashboard.
//
// Identity gate: the requester types their email, it must match a real
// employee, and they must then confirm a one-time code emailed to that
// address (sendPublicLeaveOtp / verifyPublicLeaveOtp on the backend) before
// the balance/request form unlocks. No password/login, but this proves the
// requester actually controls the email rather than just knowing it — see
// docs/features/leave.md ("Public (unauthenticated) leave request").
const PublicLeaveRequest: React.FC = () => {
  const [email, setEmail] = useState('')
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [verifiedEmployee, setVerifiedEmployee] =
    useState<PublicEmployee | null>(null)
  const [verifyToken, setVerifyToken] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState<{
    message: string
    task_id: string
    total_leave_days: number
  } | null>(null)
  const [notifyPickerOpen, setNotifyPickerOpen] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown(s => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const [sendOtp, { isLoading: isSendingOtp }] = useSendPublicLeaveOtpMutation()
  const [confirmOtp, { isLoading: isConfirmingOtp }] =
    useConfirmPublicLeaveOtpMutation()

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) {
      setVerifyError('Enter your email')
      return
    }
    setVerifyError(null)
    try {
      await sendOtp({ email: trimmed }).unwrap()
      setOtpSentTo(trimmed)
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'No employee found with that email'
      setVerifyError(message)
    }
  }

  const handleResendOtp = async () => {
    if (!otpSentTo || resendCooldown > 0) return
    try {
      await sendOtp({ email: otpSentTo }).unwrap()
      toast.success('Verification code resent')
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } }
      // Rate-limited server-side despite the client-side cooldown (e.g. a
      // page refresh reset it) - back off longer instead of leaving the
      // button re-clickable into the same wall immediately.
      if (err?.status === 429) {
        setResendCooldown(60)
      }
      toast.error(err?.data?.message || 'Failed to resend code')
    }
  }

  const handleConfirmOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = otp.trim()
    if (!trimmed) {
      setOtpError('Enter the verification code')
      return
    }
    if (!otpSentTo) return
    setOtpError(null)
    try {
      const res = await confirmOtp({
        email: otpSentTo,
        otp: trimmed,
      }).unwrap()
      setVerifiedEmployee(res.data)
      setVerifyToken(res.data.verify_token)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Incorrect verification code'
      setOtpError(message)
    }
  }

  // The verify token expires after 30 minutes (controller/publicLeave
  // .controller.js#LEAVE_VERIFY_TOKEN_TTL) — if it lapses mid-session
  // (slow form fill, balances query, submit, or report-email all use it),
  // the backend 401s. Treat that as "please verify again" instead of a
  // generic failure toast, and drop back to the email step.
  const handleVerifyTokenExpiry = (error: unknown): boolean => {
    const err = error as { status?: number }
    if (err?.status !== 401) return false
    toast.error('Verification expired — please verify your email again')
    resetIdentity()
    return true
  }

  const { data: employeesData } = useGetPublicEmployeesQuery(undefined, {
    skip: !verifiedEmployee,
  })
  const reportingManagerName = verifiedEmployee?.reporting_manager_name
    ?.trim()
    .toLowerCase()
  const notifyOptions = useMemo(
    () =>
      (employeesData?.data ?? []).filter(
        e =>
          e.user_id !== verifiedEmployee?.user_id &&
          e.user_name?.trim() &&
          e.user_name.trim().toLowerCase() !== reportingManagerName
      ),
    [employeesData, verifiedEmployee, reportingManagerName]
  )
  const [notifySearch, setNotifySearch] = useState('')
  const filteredNotifyOptions = useMemo(() => {
    const q = notifySearch.trim().toLowerCase()
    if (!q) return notifyOptions
    return notifyOptions.filter(e => e.user_name.toLowerCase().includes(q))
  }, [notifyOptions, notifySearch])

  const [createLeaveRequest, { isLoading: isSubmitting }] =
    useCreatePublicLeaveRequestMutation()
  const [uploadAttachment] = useUploadPublicLeaveAttachmentMutation()
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const [emailReport, { isLoading: isEmailingReport }] =
    useEmailPublicLeaveReportMutation()

  const formik = useFormik<PublicLeaveFormValues>({
    initialValues: {
      leave_type: LEAVE_TYPES[0] ?? '',
      from_date: '',
      from_half: 'full',
      to_date: '',
      to_half: 'full',
      reason: '',
      notify: [],
      attachment: null,
    },
    validationSchema: Yup.object({
      leave_type: Yup.string().required('Leave type is required'),
      from_date: Yup.string().required('From date is required'),
      to_date: Yup.string()
        .required('To date is required')
        .test(
          'after-from',
          'To date must be on or after the from date',
          function (value) {
            return (
              !value || !this.parent.from_date || value >= this.parent.from_date
            )
          }
        ),
      reason: Yup.string().max(2000).required('Note is required'),
      notify: Yup.array()
        .of(Yup.number().required())
        .min(1, 'Select at least one person to notify')
        .required('Notify is required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!verifiedEmployee || !verifyToken) return
      try {
        const res = await createLeaveRequest({
          employee_id: verifiedEmployee.user_id,
          leave_type: values.leave_type,
          from_date: values.from_date,
          to_date: values.to_date,
          from_half: values.from_half,
          to_half: values.to_half,
          reason: values.reason,
          notify: values.notify,
          verifyToken,
        }).unwrap()

        if (values.attachment) {
          try {
            await uploadAttachment({
              taskId: res.data.task_id,
              file: values.attachment,
            }).unwrap()
          } catch {
            toast.error(
              'Leave request submitted, but the attachment failed to upload'
            )
          }
        }

        setSubmitted({
          message: res.message,
          task_id: res.data.task_id,
          total_leave_days: res.data.total_leave_days,
        })
      } catch (error) {
        if (handleVerifyTokenExpiry(error)) return
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Failed to submit leave request'
        toast.error(message)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { data: balancesData } = useGetPublicLeaveBalancesQuery(
    {
      employee_id: verifiedEmployee?.user_id ?? 0,
      verifyToken: verifyToken ?? '',
    },
    { skip: !verifiedEmployee || !verifyToken }
  )

  const isUnpaidLeave = formik.values.leave_type === UNPAID_LEAVE_TYPE

  const availableForType = useMemo(() => {
    const rows = balancesData?.data ?? []
    return rows
      .filter(b => b.leave_type === formik.values.leave_type)
      .reduce((sum, b) => sum + b.balance, 0)
  }, [balancesData, formik.values.leave_type])

  const requestedDays = computeLeaveDaysPreview(
    formik.values.from_date,
    formik.values.to_date,
    formik.values.from_half,
    formik.values.to_half
  )

  const isSameDay =
    !!formik.values.from_date &&
    formik.values.from_date === formik.values.to_date

  // Single-day leave only needs one half-day choice — keep to_half mirrored
  // to from_half so computeLeaveDaysPreview (which looks at both) stays
  // correct even though the "To" dropdown is hidden for this case.
  useEffect(() => {
    if (isSameDay && formik.values.to_half !== formik.values.from_half) {
      formik.setFieldValue('to_half', formik.values.from_half)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameDay, formik.values.from_half])

  const handleEmailReport = async () => {
    if (!verifiedEmployee || !verifyToken) return
    try {
      const res = await emailReport({
        employee_id: verifiedEmployee.user_id,
        verifyToken,
      }).unwrap()
      toast.success(res.message || 'Leave report emailed')
    } catch (error) {
      if (handleVerifyTokenExpiry(error)) return
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to email leave report'
      toast.error(message)
    }
  }

  const resetIdentity = () => {
    setVerifiedEmployee(null)
    setVerifyToken(null)
    setEmail('')
    setVerifyError(null)
    setOtpSentTo(null)
    setOtp('')
    setOtpError(null)
    setResendCooldown(0)
    formik.resetForm()
    setSubmitted(null)
  }

  return (
    <div className='h-screen w-full flex bg-background'>
      {/* Branded panel — same split-layout idiom as Login.tsx, so this page
          fills the screen intentionally instead of a small card floating in
          empty background. Hidden on mobile like Login's. */}
      <div className='hidden lg:flex w-1/2 shrink-0 bg-black flex-col justify-center gap-10 p-12 text-white relative overflow-hidden'>
        <div className='absolute inset-0 opacity-20 pointer-events-none'>
          <div className='absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-white blur-[120px]' />
          <div className='absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-slate-800 blur-[120px]' />
        </div>
        <div className='relative z-10 flex items-center gap-2'>
          <div className='w-10 h-10 rounded-lg bg-white flex items-center justify-center'>
            <CalendarPlus className='w-5 h-5 text-black' />
          </div>
          <span className='text-2xl font-bold tracking-tighter'>
            ClickVoltz<span className='text-[#FFC107]'>.</span>
          </span>
        </div>
        <div className='relative z-10 max-w-md'>
          <h1 className='text-4xl font-extrabold tracking-tight leading-[1.1] mb-6'>
            Request leave <br />
            <span className='text-slate-400'>in a couple of clicks.</span>
          </h1>
          <p className='text-lg text-slate-400 font-medium'>
            No login needed — verify with your email, choose your dates, and
            it's routed straight to your approver.
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className='w-full lg:w-1/2 h-screen overflow-y-auto flex items-center justify-center p-4 sm:p-8'>
        <div className='w-full max-w-xl bg-background border border-border rounded-2xl shadow-xl overflow-hidden my-auto'>
          <div className='px-8 py-6 border-b border-border/50 bg-muted/10'>
            <div className='flex items-center gap-3'>
              <div className='p-2.5 bg-primary/10 rounded-xl text-primary'>
                <CalendarPlus className='w-5 h-5' />
              </div>
              <div>
                <h1 className='text-xl font-bold'>Request Leave</h1>
                <p className='text-xs text-muted-foreground'>
                  {verifiedEmployee
                    ? `Filing as ${verifiedEmployee.user_name}`
                    : otpSentTo
                      ? `Enter the code sent to ${otpSentTo}`
                      : 'Verify your email to get started — no login needed.'}
                </p>
              </div>
            </div>
          </div>

          {!otpSentTo ? (
            /* Step 1: email verification */
            <form onSubmit={handleSendOtp} className='p-8 space-y-5'>
              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                  Your Email
                </Label>
                <div className='relative'>
                  <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                  <Input
                    type='email'
                    placeholder='name@company.com'
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value)
                      setVerifyError(null)
                    }}
                    className='pl-10 h-11 rounded-xl'
                    autoFocus
                  />
                </div>
                {verifyError && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {verifyError}
                  </p>
                )}
              </div>
              <div className='flex justify-end pt-2'>
                <Button type='submit' disabled={isSendingOtp}>
                  {isSendingOtp ? 'Sending code...' : 'Continue'}
                </Button>
              </div>
            </form>
          ) : !verifiedEmployee ? (
            /* Step 2: OTP confirmation */
            <form onSubmit={handleConfirmOtp} className='p-8 space-y-5'>
              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                  Verification Code
                </Label>
                <Input
                  type='text'
                  inputMode='numeric'
                  placeholder='6-digit code'
                  value={otp}
                  onChange={e => {
                    setOtp(e.target.value)
                    setOtpError(null)
                  }}
                  className='h-11 rounded-xl tracking-[0.3em] text-center font-semibold'
                  maxLength={6}
                  autoFocus
                />
                {otpError && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {otpError}
                  </p>
                )}
              </div>
              <div className='flex items-center justify-between gap-3 pt-2'>
                <div className='flex items-center gap-3'>
                  <button
                    type='button'
                    onClick={resetIdentity}
                    className='text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-2'
                  >
                    Use a different email
                  </button>
                  <button
                    type='button'
                    onClick={handleResendOtp}
                    disabled={isSendingOtp || resendCooldown > 0}
                    className='text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed'
                  >
                    {resendCooldown > 0
                      ? `Resend code in ${resendCooldown}s`
                      : 'Resend code'}
                  </button>
                </div>
                <Button type='submit' disabled={isConfirmingOtp}>
                  {isConfirmingOtp ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </form>
          ) : submitted ? (
            <div className='p-8 space-y-4'>
              <div className='rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-4 text-sm text-emerald-700'>
                {submitted.message}
              </div>
              <p className='text-xs text-muted-foreground'>
                {submitted.total_leave_days} day(s) requested — ClickUp task{' '}
                {submitted.task_id}
              </p>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  formik.resetForm()
                  setSubmitted(null)
                }}
              >
                Submit another request
              </Button>
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit} className='p-8 space-y-5'>
              {/* Leave Type */}
              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                  Select type of leave you want to apply
                </Label>
                <Select
                  value={formik.values.leave_type}
                  onValueChange={v => formik.setFieldValue('leave_type', v)}
                >
                  <SelectTrigger className='h-11 rounded-xl'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map(t => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className='text-xs text-muted-foreground'>
                  {isUnpaidLeave
                    ? 'Unlimited — no balance required'
                    : `${availableForType} day(s) available`}
                </p>
              </div>

              {/* From / To dates with half-day selectors */}
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                    From
                  </Label>
                  <DatePicker
                    value={formik.values.from_date}
                    onChange={v => formik.setFieldValue('from_date', v)}
                    placeholder='Pick date'
                  />
                  <Select
                    value={formik.values.from_half}
                    onValueChange={v => {
                      formik.setFieldValue('from_half', v)
                      if (isSameDay) formik.setFieldValue('to_half', v)
                    }}
                  >
                    <SelectTrigger className='h-9 rounded-xl text-xs'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HALF_DAY_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formik.touched.from_date && formik.errors.from_date && (
                    <p className='text-[11px] font-semibold text-red-500'>
                      {formik.errors.from_date}
                    </p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                    To
                  </Label>
                  <DatePicker
                    value={formik.values.to_date}
                    onChange={v => formik.setFieldValue('to_date', v)}
                    placeholder='Pick date'
                    minDate={formik.values.from_date || undefined}
                  />
                  {!isSameDay && (
                    <Select
                      value={formik.values.to_half}
                      onValueChange={v => formik.setFieldValue('to_half', v)}
                    >
                      <SelectTrigger className='h-9 rounded-xl text-xs'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HALF_DAY_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {formik.touched.to_date && formik.errors.to_date && (
                    <p className='text-[11px] font-semibold text-red-500'>
                      {formik.errors.to_date}
                    </p>
                  )}
                </div>
              </div>

              {requestedDays !== null && (
                <p className='text-sm font-medium text-foreground bg-primary/5 border border-primary/10 rounded-lg px-3 py-2'>
                  You are requesting {requestedDays} day(s) of leave
                  {!isUnpaidLeave && requestedDays > availableForType && (
                    <span className='text-destructive font-semibold'>
                      {' '}
                      — exceeds available balance
                    </span>
                  )}
                </p>
              )}

              {/* Reason */}
              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                  Note <span className='text-red-500'>*</span>
                </Label>
                <textarea
                  name='reason'
                  rows={3}
                  placeholder='Brief reason for the leave...'
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className='flex w-full rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm transition-all focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none min-h-[80px]'
                />
                {formik.touched.reason && formik.errors.reason && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {formik.errors.reason}
                  </p>
                )}
              </div>

              {/* Notify */}
              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                  Notify <span className='text-red-500'>*</span>
                </Label>
                <p className='text-[11px] text-muted-foreground'>
                  No need to add your reporting manager here, they're notified
                  automatically.
                </p>
                <Popover
                  open={notifyPickerOpen}
                  onOpenChange={open => {
                    setNotifyPickerOpen(open)
                    if (!open) setNotifySearch('')
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      className='w-full justify-between h-11 rounded-xl font-normal'
                    >
                      <div className='flex items-center gap-2'>
                        <Users className='w-4 h-4 text-muted-foreground' />
                        <span className='text-sm'>
                          {formik.values.notify.length === 0
                            ? 'Choose people to notify...'
                            : `${formik.values.notify.length} selected`}
                        </span>
                      </div>
                      <ChevronsUpDown className='w-4 h-4 text-muted-foreground opacity-50' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className='w-[var(--radix-popover-trigger-width)] p-0 rounded-xl overflow-hidden'
                    align='start'
                  >
                    <div className='p-2 border-b border-border'>
                      <Input
                        autoFocus
                        placeholder='Search employees...'
                        value={notifySearch}
                        onChange={e => setNotifySearch(e.target.value)}
                        className='h-9 text-xs'
                      />
                    </div>
                    <div className='p-1 max-h-[220px] overflow-y-auto'>
                      {filteredNotifyOptions.length === 0 ? (
                        <div className='py-4 text-center text-xs text-muted-foreground'>
                          No employees found
                        </div>
                      ) : (
                        filteredNotifyOptions.map(emp => {
                          const selected = formik.values.notify.includes(
                            emp.user_id
                          )
                          return (
                            <button
                              key={emp.user_id}
                              type='button'
                              onClick={() =>
                                formik.setFieldValue(
                                  'notify',
                                  selected
                                    ? formik.values.notify.filter(
                                        id => id !== emp.user_id
                                      )
                                    : [...formik.values.notify, emp.user_id]
                                )
                              }
                              className={cn(
                                'w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left',
                                selected &&
                                  'bg-accent/50 text-primary font-medium'
                              )}
                            >
                              <div
                                className={cn(
                                  'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                                  selected
                                    ? 'bg-primary border-primary'
                                    : 'border-muted-foreground/30'
                                )}
                              >
                                {selected && (
                                  <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
                                )}
                              </div>
                              <span className='truncate'>{emp.user_name}</span>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                {formik.values.notify.length > 0 && (
                  <div className='flex flex-wrap gap-1.5 pt-1'>
                    {formik.values.notify.map(id => {
                      const emp = notifyOptions.find(e => e.user_id === id)
                      return (
                        <span
                          key={id}
                          className='inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-muted text-xs font-medium'
                        >
                          {emp?.user_name ?? `#${id}`}
                          <button
                            type='button'
                            onClick={() =>
                              formik.setFieldValue(
                                'notify',
                                formik.values.notify.filter(v => v !== id)
                              )
                            }
                            className='hover:text-destructive'
                          >
                            <X className='w-3 h-3' />
                          </button>
                        </span>
                      )
                    })}
                  </div>
                )}
                {formik.touched.notify && formik.errors.notify && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {typeof formik.errors.notify === 'string'
                      ? formik.errors.notify
                      : 'Select at least one person to notify'}
                  </p>
                )}
              </div>

              {/* Attachment — optional, mirrors the attachment field on
                  ClickUp's own leave request form (e.g. a medical
                  certificate). Uploaded to the ClickUp task's native
                  Attachments after the request is created
                  (routes/publicLeave.routes.js). */}
              <div className='space-y-2'>
                <Label className='block text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                  Attachment
                </Label>
                <input
                  type='file'
                  ref={attachmentInputRef}
                  className='hidden'
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) {
                      if (file.size > MAX_ATTACHMENT_SIZE) {
                        toast.error('File size exceeds 10MB limit')
                      } else {
                        formik.setFieldValue('attachment', file)
                      }
                    }
                    if (attachmentInputRef.current) {
                      attachmentInputRef.current.value = ''
                    }
                  }}
                />
                {formik.values.attachment ? (
                  <span className='inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-lg bg-muted text-xs font-medium max-w-full'>
                    <Paperclip className='w-3.5 h-3.5 shrink-0 text-muted-foreground' />
                    <span className='truncate'>
                      {formik.values.attachment.name}
                    </span>
                    <button
                      type='button'
                      onClick={() => formik.setFieldValue('attachment', null)}
                      className='hover:text-destructive shrink-0'
                    >
                      <X className='w-3 h-3' />
                    </button>
                  </span>
                ) : (
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => attachmentInputRef.current?.click()}
                    className='h-9 rounded-xl text-xs gap-1.5'
                  >
                    <Paperclip className='w-3.5 h-3.5' />
                    Attach File
                  </Button>
                )}
              </div>

              <div className='flex items-center justify-between gap-3 pt-4 border-t border-border/50'>
                <div className='flex items-center gap-3'>
                  <button
                    type='button'
                    onClick={resetIdentity}
                    className='text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-2'
                  >
                    Not you?
                  </button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='gap-1.5 text-xs'
                    onClick={handleEmailReport}
                    disabled={isEmailingReport}
                  >
                    <Mail className='w-3.5 h-3.5' />
                    {isEmailingReport ? 'Sending...' : 'Email My Report'}
                  </Button>
                </div>
                <Button type='submit' disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Request'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default PublicLeaveRequest
